// Die Listentabelle der Anwendung (ST-UX-001, Entwurf v4 `table.list`).
//
// Eine Komponente fuer jede Liste gleichartiger Datensaetze mit Spalten
// (Kontakte, spaeter Sammlungen). Fluents Table liefert echte <table>-,
// <th>- und <td>-Elemente; die Spaltenkoepfe tragen scope="col", die erste
// Datenspalte ist die Zeilenueberschrift (scope="row"), damit ein Screenreader
// beim Wandern durch eine Spalte den Namen der Zeile mitliest (WCAG 1.3.1).
//
// AUSWAHL OHNE FLUENTS TableSelectionCell: deren Kaestchen hat keinen eigenen
// Namen. Hier heisst jedes Zeilenkaestchen "<Name> auswaehlen" und das
// Kopfkaestchen so, wie der Aufrufer es nennt -- die Namen, die Proben und
// Hilfsmittel seit dem Auswahl-Schnitt kennen.
//
// SCHMAL BLEIBT ES EINE TABELLE. Die Spalten fallen in zwei Stufen weg,
// gemessen an der BREITE DER TABELLE (Containerabfrage) und nicht am Fenster:
// dieselbe Tabelle steht neben der Navigation schmaler als das Fenster
// (sonst brachen Namen bei 1024 px mitten im Wort). Unter 56 rem
// gehen die Spalten der Stufe `mittel`, unter 40 rem auch die der Stufe
// `schmal`; ihr Inhalt erscheint ueber `kurz` als Kleintext oder ueber
// `gefaltet` als Element (Pille, Knopf) in der Zeilenueberschrift. Nie beides
// zugleich: die ausgeblendete Zelle und das ausgeblendete Gefaltete sind
// display:none und damit nicht im Barrierebaum. Eine Spalte ohne Stufe bleibt
// immer -- bei 320 px schneidet sie dann ab; jede Spalte ausser der ersten
// braucht deshalb eine Stufe (Probe reflow.spec, Kanten).
// So laeuft bei 320 px nichts waagerecht ueber (WCAG 1.4.10), ohne dass eine
// Liste mit eigener Semantik daneben gepflegt werden muesste.
//
// UMBRUCH NUR AN LEERZEICHEN. Ein hartes Umbrechen ueberall liess die Spalten
// auf wenige Zeichen schrumpfen; nur eine Spalte mit `umbruch: 'hart'` (eine
// E-Mail-Adresse, ein Wort ohne Trennstelle) darf mitten im Wort umbrechen.

import { memo, useId, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';

/** Die Tabelle misst sich an ihrem Rahmen; unterhalb dieser Breiten fallen Spalten weg. */
const MITTEL = '@container tabelle (max-width: 56rem)';
const SCHMAL = '@container tabelle (max-width: 40rem)';

export interface TabellenSpalte<T> {
  readonly id: string;
  readonly kopf: string;
  readonly zelle: (zeile: T) => ReactNode;
  /** Ab welcher Tabellenbreite die Spalte wegfaellt; ohne Angabe bleibt sie immer. */
  readonly stufe?: 'mittel' | 'schmal';
  /** Der Inhalt als Kleintext unter dem Namen, solange die Spalte fehlt; leer heisst nichts. */
  readonly kurz?: (zeile: T) => string | undefined;
  /** Der Inhalt als Element (Pille, Knopf) unter dem Namen, solange die Spalte fehlt. */
  readonly gefaltet?: (zeile: T) => ReactNode;
  /** `hart`: darf mitten im Wort umbrechen (E-Mail-Adresse). Sonst nur an Leerzeichen. */
  readonly umbruch?: 'hart';
}

export interface TabellenAuswahl<T> {
  readonly gewaehlt: (zeile: T) => boolean;
  readonly umschalten: (zeile: T) => void;
  /** Der Name des Zeilenkaestchens, etwa "Maria Beispiel auswählen". */
  readonly name: (zeile: T) => string;
  /** Fehlt er, bleibt der Kopf der Auswahlspalte ein stummer Spaltenname. */
  readonly kopf?: {
    readonly name: string;
    readonly stand: boolean | 'mixed';
    readonly umschalten: () => void;
  };
  /**
   * Waehrend einer laufenden Aktion (Entfernen, Rueckgaengig) aendert sich die
   * Auswahl nicht. Die Kaestchen bleiben fokussierbar und sagen es
   * (aria-disabled); ein natives disabled wuerfe einen Fokus darauf auf BODY.
   * Ohne Angabe: nicht gesperrt.
   */
  readonly gesperrt?: boolean;
}

/**
 * Eine aufklappbare Zeile (Disclosure nach APG): ein Knopf in der
 * Zeilenueberschrift mit aria-expanded, darunter eine Folgezeile ueber alle
 * Spalten. Optional -- ohne `aufklappen` ist die Tabelle wie vorher.
 */
export interface TabellenAufklappen<T> {
  /** Kann diese Zeile aufklappen? */
  readonly kann: (zeile: T) => boolean;
  /** Der sichtbare Text des Knopfs, etwa "Personen"; je Zeile, wenn Zeilen verschiedener Art aufklappen. */
  readonly knopf: string | ((zeile: T) => string);
  /**
   * Der volle Name des Knopfs; beginnt mit dem sichtbaren Text (WCAG 2.5.3).
   * `anzahl` ist, was der Inhalt nach dem Laden gemeldet hat (siehe `inhalt`).
   */
  readonly name: (zeile: T, anzahl?: string) => string;
  /**
   * Der Inhalt der Folgezeile; erst beim Aufklappen gezeichnet. Ueber `melden`
   * kann er nach dem Laden eine Anzahl nennen; der Knopf zeigt sie dann als
   * "<knopf> · <anzahl>", auch zugeklappt. Die Zeile haelt sie selbst, weil sie
   * memoisiert ist und eine Anzahl beim Aufrufer sie nicht neu zeichnete.
   */
  readonly inhalt: (zeile: T, melden: (anzahl: string | undefined) => void) => ReactNode;
}

const useStyles = makeStyles({
  rahmen: { containerType: 'inline-size', containerName: 'tabelle', width: '100%' },
  tabelle: {
    width: '100%',
    // Fluents Tabelle verteilt die Breite gleich (fixed); hier bestimmt der
    // Inhalt sie, sonst bekaeme "Status" so viel Platz wie der Name.
    tableLayout: 'auto',
    '& td, & th': { verticalAlign: 'middle' },
  },
  hart: { overflowWrap: 'anywhere', wordBreak: 'break-word' },
  kopfzeile: {
    '& th': {
      color: tokens.colorNeutralForeground3,
      fontSize: tokens.fontSizeBase200,
      fontWeight: tokens.fontWeightSemibold,
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  zeile: {
    // v4 `--row`: 44 px, zugleich das Fingerziel der Zeile.
    minHeight: '2.75rem',
    height: '2.75rem',
  },
  auswahl: { width: '2.75rem', paddingInlineEnd: 0 },
  gesperrt: { opacity: 0.5, '& input': { cursor: 'not-allowed' } },
  // v4 `.listfoot`: Zahl und "Weitere laden" am unteren Rand der Karte.
  fuss: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem 0.75rem',
    padding: '0.625rem 0.75rem',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: 'tabular-nums',
  },
  zeilenkopf: {
    fontWeight: 'inherit',
    textAlign: 'start',
    paddingInline: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalXS,
  },
  // Die Mindestbreite am INHALT: an einer Tabellenzelle wirkt min-width nicht.
  // 12 rem tragen einen zweiteiligen Namen ohne Umbruch.
  zeilenkopfInhalt: { minWidth: '12rem', [SCHMAL]: { minWidth: 0 } },
  ausMittel: { [MITTEL]: { display: 'none' } },
  ausSchmal: { [SCHMAL]: { display: 'none' } },
  zusatz: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: '0.75rem',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  // Der Knopf darf zwischen Woertern umbrechen, aber nicht im Wort: "Organisatione|n" las sich
  // falsch (DESIGN ansprech-knopf). Die Knopfworte sind kurz genug, dass die Spalte sie bei
  // 320 px und 200 % ganz aufnimmt, ohne die Tabelle in den Innenrand der Karte zu schieben
  // (Proben kanten-320.spec.ts).
  aufklappKnopf: { marginTop: '0.125rem', minHeight: '2rem', minWidth: 0, maxWidth: '100%', height: 'auto', whiteSpace: 'normal', overflowWrap: 'normal', textAlign: 'start' },
  folgezeile: { backgroundColor: tokens.colorNeutralBackground2 },
  // Fluents Zeile setzt display selbst; das hidden-Attribut allein bliebe wirkungslos.
  zu: { display: 'none' },
  // Eingerueckt unter den Namen: die Folgezeile gehoert sichtbar zu ihrer Zeile. Hoechstens 15 %
  // der Zeile: 3.5rem sind bei 200 % 112 px und schoben die Tabelle bei 320 px ueber die Karte
  // (DESIGN-P1 zu #771); breit bleibt es bei 3.5rem.
  folgeInhalt: { paddingBlock: tokens.spacingVerticalS, paddingInlineStart: 'min(3.5rem, 15%)' },
  // Die gesteuerte Folgezeile beginnt buendig mit dem Namen; eingerueckt wuerde ein Feld darin bei 320 px zu schmal.
  gesteuertInhalt: { paddingBlock: tokens.spacingVerticalS },
  // Der Kleintext einer Spalte erscheint genau dann, wenn sie fehlt.
  kurzMittel: { display: 'none', overflowWrap: 'anywhere', [MITTEL]: { display: 'inline' } },
  kurzSchmal: { display: 'none', overflowWrap: 'anywhere', [SCHMAL]: { display: 'inline' } },
  // Gefaltete Elemente stehen in einer eigenen Reihe unter dem Kleintext. Die Reihe selbst
  // erscheint erst an der Grenze ihrer fruehesten Stufe; breit traegt sie sonst leer ihren Rand.
  gefaltet: { flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem 0.5rem', marginTop: '0.25rem' },
  gefaltetAbMittel: { display: 'none', [MITTEL]: { display: 'flex' } },
  gefaltetAbSchmal: { display: 'none', [SCHMAL]: { display: 'flex' } },
  faltMittel: { display: 'none', [MITTEL]: { display: 'contents' } },
  faltSchmal: { display: 'none', [SCHMAL]: { display: 'contents' } },
  nurLeser: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
});

/**
 * Eine Zeile, nur neu gezeichnet, wenn sich IHR Datensatz oder IHR Haken
 * aendert. Eine Liste mit 251 Zeilen zeichnete sonst bei jedem Fortschritts-
 * schritt eines Dialogs alle Zeilen neu, und der Dialog wurde unbedienbar
 * (gemessen, Proben A78.7/A78.8). Die Rueckrufe kommen ueber eine Referenz,
 * damit ihre neue Identitaet je Darstellung die Zeile nicht neu zeichnet.
 */
interface ZeilenProps<T> {
  readonly zeile: T;
  readonly gewaehlt: boolean | undefined;
  readonly gesperrt: boolean;
  /** Der Inhalt der vom Aufrufer gesteuerten Folgezeile; undefined, solange sie zu ist. */
  readonly folge: ReactNode;
  readonly spalten: readonly TabellenSpalte<T>[];
  readonly aktuell: { readonly current: ZeilenRueckrufe<T> };
  readonly styles: ReturnType<typeof useStyles>;
}

interface ZeilenRueckrufe<T> {
  readonly auswahl: TabellenAuswahl<T> | undefined;
  readonly aufklappen: TabellenAufklappen<T> | undefined;
}

function stufenKlasse(stufe: TabellenSpalte<unknown>['stufe'], styles: ReturnType<typeof useStyles>): string | undefined {
  return stufe === 'mittel' ? styles.ausMittel : stufe === 'schmal' ? styles.ausSchmal : undefined;
}

function ZeileOhneMemo<T>({ zeile, gewaehlt, gesperrt, folge, spalten, aktuell, styles }: ZeilenProps<T>): ReactNode {
  const [erste, ...weitere] = spalten;
  const { auswahl, aufklappen } = aktuell.current;
  const [offen, setOffen] = useState(false);
  const [anzahl, setAnzahl] = useState<string | undefined>(undefined);
  const folgeId = useId();
  const klappbar = aufklappen !== undefined && aufklappen.kann(zeile);
  const kurz = weitere.flatMap((spalte) => {
    const text = spalte.stufe === undefined ? undefined : spalte.kurz?.(zeile);
    return text === undefined || text.length === 0 ? [] : [{ id: spalte.id, text, stufe: spalte.stufe }];
  });
  const gefaltet = weitere.flatMap((spalte) => {
    const element = spalte.stufe === undefined ? undefined : spalte.gefaltet?.(zeile);
    return element === undefined || element === null || element === '' ? [] : [{ id: spalte.id, element, stufe: spalte.stufe }];
  });
  const hauptzeile = (
    <TableRow className={styles.zeile}>
      {gewaehlt !== undefined && auswahl !== undefined ? (
        <TableCell className={styles.auswahl}>
          <Checkbox
            aria-label={auswahl.name(zeile)}
            checked={gewaehlt}
            className={gesperrt ? styles.gesperrt : undefined}
            {...(gesperrt ? { 'aria-disabled': true } : {})}
            onChange={() => {
              if (!gesperrt) aktuell.current.auswahl?.umschalten(zeile);
            }}
          />
        </TableCell>
      ) : undefined}
      {/* Fluents TableCell kennt nur td; die Zeilenueberschrift ist ein
          eigenes th mit denselben Abstaenden. */}
      <th scope="row" className={styles.zeilenkopf}>
        <div className={styles.zeilenkopfInhalt}>
          {erste?.zelle(zeile)}
          {klappbar ? (
            <div>
              <Button
                appearance="subtle"
                size="small"
                className={styles.aufklappKnopf}
                aria-expanded={offen}
                aria-controls={folgeId}
                aria-label={aufklappen.name(zeile, anzahl)}
                icon={<span aria-hidden="true">{offen ? '▾' : '▸'}</span>}
                onClick={() => setOffen((vorher) => !vorher)}
              >
                {(() => {
                  const knopf = typeof aufklappen.knopf === 'string' ? aufklappen.knopf : aufklappen.knopf(zeile);
                  return anzahl === undefined ? knopf : `${knopf} · ${anzahl}`;
                })()}
              </Button>
            </div>
          ) : undefined}
          {kurz.length > 0 ? (
            <div className={styles.zusatz}>
              {kurz.map((eintrag) => (
                <span key={eintrag.id} className={eintrag.stufe === 'mittel' ? styles.kurzMittel : styles.kurzSchmal}>
                  {eintrag.text}
                </span>
              ))}
            </div>
          ) : undefined}
          {gefaltet.length > 0 ? (
            <div className={mergeClasses(styles.gefaltet, gefaltet.some((eintrag) => eintrag.stufe === 'mittel') ? styles.gefaltetAbMittel : styles.gefaltetAbSchmal)}>
              {gefaltet.map((eintrag) => (
                <span key={eintrag.id} className={eintrag.stufe === 'mittel' ? styles.faltMittel : styles.faltSchmal}>
                  {eintrag.element}
                </span>
              ))}
            </div>
          ) : undefined}
        </div>
      </th>
      {weitere.map((spalte) => (
        <TableCell key={spalte.id} className={mergeClasses(stufenKlasse(spalte.stufe, styles), spalte.umbruch === 'hart' && styles.hart)}>
          {spalte.zelle(zeile)}
        </TableCell>
      ))}
    </TableRow>
  );
  const breite = spalten.length + (gewaehlt !== undefined ? 1 : 0);
  const gesteuert =
    folge === undefined || folge === null ? undefined : (
      <TableRow className={styles.folgezeile}>
        <TableCell colSpan={breite} className={styles.gesteuertInhalt}>
          {folge}
        </TableCell>
      </TableRow>
    );
  if (!klappbar) {
    return gesteuert === undefined ? hauptzeile : (
      <>
        {hauptzeile}
        {gesteuert}
      </>
    );
  }
  // Die Folgezeile steht immer im Baum, damit aria-controls auf ein Element zeigt;
  // zu ist sie ausgeblendet und ihr Inhalt nicht gezeichnet.
  return (
    <>
      {hauptzeile}
      <TableRow id={folgeId} className={mergeClasses(styles.folgezeile, !offen && styles.zu)} hidden={!offen}>
        <TableCell colSpan={breite} className={styles.folgeInhalt}>
          {offen ? aufklappen.inhalt(zeile, setAnzahl) : undefined}
        </TableCell>
      </TableRow>
      {gesteuert}
    </>
  );
}

const Zeile = memo(ZeileOhneMemo) as typeof ZeileOhneMemo;

export function Tabelle<T>({
  name,
  zeilen,
  schluessel,
  spalten,
  auswahl,
  aufklappen,
  folgezeile,
  fuss,
}: {
  /** Der zugaengliche Name der Tabelle. */
  readonly name: string;
  readonly zeilen: readonly T[];
  readonly schluessel: (zeile: T) => string;
  /** Die erste Spalte ist die Zeilenueberschrift. */
  readonly spalten: readonly TabellenSpalte<T>[];
  readonly auswahl?: TabellenAuswahl<T> | undefined;
  readonly aufklappen?: TabellenAufklappen<T> | undefined;
  /**
   * Eine Folgezeile ueber alle Spalten, die der Aufrufer oeffnet und schliesst, etwa eine
   * Rueckfrage an der Zeile. Liefert sie undefined, steht keine da; zu bleibt die Zeile
   * memoisiert, offen zeichnet sie mit jedem Aufruf neu.
   */
  readonly folgezeile?: ((zeile: T) => ReactNode) | undefined;
  /** Die Fusszeile unter der Tabelle (v4 `.listfoot`), etwa Zahl und "Weitere laden". */
  readonly fuss?: ReactNode;
}): ReactNode {
  const styles = useStyles();
  const aktuell = useRef<ZeilenRueckrufe<T>>({ auswahl, aufklappen });
  aktuell.current = { auswahl, aufklappen };
  const gesperrt = auswahl?.gesperrt === true;
  if (spalten.length === 0) {
    return undefined;
  }
  return (
    <div className={styles.rahmen}>
    <Table aria-label={name} className={styles.tabelle}>
      <TableHeader className={styles.kopfzeile}>
        <TableRow>
          {auswahl !== undefined ? (
            <TableHeaderCell scope="col" className={styles.auswahl}>
              {auswahl.kopf !== undefined ? (
                <Checkbox
                  aria-label={auswahl.kopf.name}
                  checked={auswahl.kopf.stand}
                  className={gesperrt ? styles.gesperrt : undefined}
                  {...(gesperrt ? { 'aria-disabled': true } : {})}
                  onChange={() => {
                    if (!gesperrt) auswahl.kopf?.umschalten();
                  }}
                />
              ) : (
                <span className={styles.nurLeser}>Auswahl</span>
              )}
            </TableHeaderCell>
          ) : undefined}
          {spalten.map((spalte) => (
            <TableHeaderCell key={spalte.id} scope="col" className={stufenKlasse(spalte.stufe, styles)}>
              {spalte.kopf}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {zeilen.map((zeile) => (
          <Zeile
            key={schluessel(zeile)}
            zeile={zeile}
            gewaehlt={auswahl?.gewaehlt(zeile)}
            gesperrt={gesperrt}
            folge={folgezeile?.(zeile)}
            spalten={spalten}
            aktuell={aktuell}
            styles={styles}
          />
        ))}
      </TableBody>
    </Table>
    {fuss === undefined ? undefined : <div className={styles.fuss}>{fuss}</div>}
    </div>
  );
}
